import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LottieName } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';
import BaseTooltip from './BaseTooltip';
import { useLottieUpdate } from '../hooks/useLottieUpdate';
import appLogger from '@/services/logger';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { LOTTIE_OPTIONS } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import Icon from '@/components/atoms/Icon';
import IconButton from '@/components/atoms/Button/IconButton';

interface LottieTooltipProps {
  element: {
    id: string;
    name: string;
    type: string;
    lottie_name: LottieName;
    hex_colors: string[];
  };
  triggerRef: React.RefObject<HTMLDivElement>;
  onDelete?: (elementId: string) => void;
}

export const LottieTooltip: React.FC<LottieTooltipProps> = ({ element, triggerRef, onDelete }) => {
  const { clipId } = useClipsContext();
  const { updateLottieElement } = useLottieUpdate();
  const [selectedLottieName, setSelectedLottieName] = useState<LottieName>(element.lottie_name);
  const [colors, setColors] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<string>('');

  // Helper function to ensure hex color has # prefix
  const ensureHexPrefix = useCallback((color: string): string => {
    return color.startsWith('#') ? color : `#${color}`;
  }, []);

  // Initialize and sync colors from element
  useEffect(() => {
    const elementColors = element.hex_colors || ['FF0000', '03FF00', 'FF7400'];
    const formattedColors = elementColors.map(ensureHexPrefix);

    // Ensure we have exactly 3 colors
    if (formattedColors.length !== 3) {
      setColors(['#FF0000', '#03FF00', '#FF7400']);
    } else {
      setColors(formattedColors);
    }
  }, [element.hex_colors, ensureHexPrefix]);

  // Sync selected lottie name when element changes
  useEffect(() => {
    setSelectedLottieName(element.lottie_name);
  }, [element.lottie_name]);

  // Helper function to format colors for backend (remove # prefix if present)
  const formatColorsForBackend = useCallback((colors: string[]): string[] => {
    return colors.map(color => color.replace('#', ''));
  }, []);

  // Function to update lottie element
  const updateElement = useCallback(
    async (newLottieName: LottieName, newColors: string[]) => {
      if (
        (newLottieName !== element.lottie_name || JSON.stringify(newColors) !== JSON.stringify(element.hex_colors)) &&
        !isUpdating
      ) {
        // Create a unique key for this update to prevent duplicate calls
        const updateKey = `${newLottieName}-${JSON.stringify(newColors)}`;

        if (lastUpdateRef.current === updateKey) {
          return; // Skip if this exact update was already sent
        }

        setIsUpdating(true);
        try {
          await updateLottieElement(clipId, element.id, {
            lottie_name: newLottieName,
            hex_colors: formatColorsForBackend(newColors)
          });
          lastUpdateRef.current = updateKey;
        } catch (error) {
          appLogger.error('Failed to update lottie element', error);
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [
      element.lottie_name,
      element.hex_colors,
      clipId,
      element.id,
      updateLottieElement,
      isUpdating,
      formatColorsForBackend
    ]
  );

  // Debounced update function
  const debouncedUpdate = useCallback(
    (newLottieName: LottieName, newColors: string[]) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        updateElement(newLottieName, newColors);
      }, 300);
    },
    [updateElement]
  );

  // Immediate update function (for select changes)
  const immediateUpdate = useCallback(
    (newLottieName: LottieName, newColors: string[]) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      updateElement(newLottieName, newColors);
    },
    [updateElement]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleLottieNameChange = useCallback(
    (value: LottieName) => {
      setSelectedLottieName(value);
      setIsDropdownOpen(false);
      immediateUpdate(value, colors);
    },
    [colors, immediateUpdate]
  );

  const handleColorChange = useCallback(
    (colorIndex: number, newColor: string) => {
      const newColors = [...colors];
      newColors[colorIndex] = newColor;
      setColors(newColors);
      debouncedUpdate(selectedLottieName, newColors);
    },
    [colors, selectedLottieName, debouncedUpdate]
  );

  const handleDelete = useCallback(
    (closeTooltip: () => void) => {
      if (onDelete) {
        onDelete(element.id);
        closeTooltip();
      }
    },
    [element.id, onDelete]
  );

  return (
    <BaseTooltip triggerRef={triggerRef}>
      {closeTooltip => (
        <div className="min-w-0 max-w-xs space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-gray-800">Motion Graphic</h3>
            {onDelete && (
              <IconButton
                icon="IconTrash"
                title="Delete Animation"
                onMouseDown={() => handleDelete(closeTooltip)}
                trackingId="delete-lottie-button"
                variation="text"
                size="small"
              />
            )}
          </div>

          {/* Animation Type Dropdown */}
          <div className="relative">
            <button
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className="flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Icon name="IconStars" size={16} className="shrink-0 text-gray-600" />
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-medium text-gray-900">
                    {LOTTIE_OPTIONS.find(option => option.value === selectedLottieName)?.label || 'Crazy Swirl'}
                  </div>
                </div>
              </div>
              <Icon name="IconChevronDown" size={16} className="shrink-0 text-gray-500" />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {LOTTIE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLottieNameChange(option.value);
                    }}
                    className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                      selectedLottieName === option.value ? 'bg-card-hover-bg' : ''
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Colors Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-400">Colors</h4>
            <div className="space-y-2">
              {colors.map((color, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">Color {index + 1}</span>
                    <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-600">
                      {color.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex size-8 items-center gap-2">
                    <ColorPicker
                      hex={color}
                      onChange={newColor => handleColorChange(index, newColor)}
                      classNames="size-8 rounded-full border border-gray-200 cursor-pointer"
                      pickerClassNames="right-0 top-0 z-50"
                    >
                      <div className="size-full rounded-full" style={{ backgroundColor: color }} />
                    </ColorPicker>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isUpdating && (
            <div className="text-center">
              <span className="text-xs text-gray-500">Updating...</span>
            </div>
          )}
        </div>
      )}
    </BaseTooltip>
  );
};
